import { useState } from 'react';
import { AskRequestStatus } from '@hone-automation/common';
import { usePatchQueueCommentMutation, useDeleteQueueCommentMutation } from 'hooks/queue';
import { useQueueContext } from 'presentation/components/Queue/QueueContext';

export interface UseMessageActionsOptions {
  messageId: string;
  status: AskRequestStatus;
  locationId: string;
  onEditSuccess?: (id: string, content: string) => void;
  onDeleteSuccess?: (id: string) => void;
}

export interface UseMessageActionsReturn {
  isEditing: boolean;
  showDeleteModal: boolean;
  isDeleting: boolean;
  isUpdating: boolean;
  handleEdit: () => void;
  handleDelete: () => void;
  handleSaveEdit: (content: string) => void;
  handleConfirmDelete: () => void;
  handleCancelEdit: () => void;
  handleCancelDelete: () => void;
}

/**
 * Custom hook for managing message edit and delete operations
 * Handles state management, mutations, and queue context updates
 */
export const useMessageActions = ({
  messageId,
  status,
  locationId,
  onEditSuccess,
  onDeleteSuccess,
}: UseMessageActionsOptions): UseMessageActionsReturn => {
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const {
    state: { requests, selectedItem },
    dispatch,
  } = useQueueContext();

  // Patch comment mutation with optimistic UI update
  const patchCommentMutation = usePatchQueueCommentMutation(status, locationId, data => {
    // Success callback - update the comment in the queue context
    if (onEditSuccess) {
      onEditSuccess(messageId, data.comment);
    }

    // Update the comment in requests list
    const updatedRequests = requests.map(req => {
      if (req.id === selectedItem?.id) {
        return {
          ...req,
          comments:
            req.comments?.map(comment =>
              comment.id === messageId
                ? {
                    ...comment,
                    comment: data.comment,
                    updatedAt: new Date(),
                  }
                : comment
            ) || [],
        };
      }
      return req;
    });

    dispatch({
      type: 'SET_REQUESTS',
      payload: updatedRequests,
    });

    // Update selected item
    if (selectedItem) {
      const updatedSelectedItem = {
        ...selectedItem,
        comments:
          selectedItem.comments?.map(comment =>
            comment.id === messageId ? { ...comment, comment: data.comment, updatedAt: new Date() } : comment
          ) || [],
      };

      dispatch({
        type: 'SET_SELECTED_ITEM',
        payload: updatedSelectedItem,
      });
    }
  });

  // Delete comment mutation with optimistic UI update
  const deleteCommentMutation = useDeleteQueueCommentMutation(status, locationId, () => {
    // Success callback - remove the comment from the queue context
    if (onDeleteSuccess) {
      onDeleteSuccess(messageId);
    }

    // Remove the comment from requests list
    const updatedRequests = requests.map(req => {
      if (req.id === selectedItem?.id) {
        return {
          ...req,
          comments: req.comments?.filter(comment => comment.id !== messageId) || [],
        };
      }
      return req;
    });

    dispatch({
      type: 'SET_REQUESTS',
      payload: updatedRequests,
    });

    // Update selected item
    if (selectedItem) {
      const updatedSelectedItem = {
        ...selectedItem,
        comments: selectedItem.comments?.filter(comment => comment.id !== messageId) || [],
      };

      dispatch({
        type: 'SET_SELECTED_ITEM',
        payload: updatedSelectedItem,
      });
    }
  });

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleSaveEdit = (content: string) => {
    patchCommentMutation.mutate({
      id: messageId,
      comment: content,
    });
    setIsEditing(false);
  };

  const handleConfirmDelete = () => {
    deleteCommentMutation.mutate(messageId);
    setShowDeleteModal(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  return {
    isEditing,
    showDeleteModal,
    isDeleting: deleteCommentMutation.isPending || false,
    isUpdating: patchCommentMutation.isPending || false,
    handleEdit,
    handleDelete,
    handleSaveEdit,
    handleConfirmDelete,
    handleCancelEdit,
    handleCancelDelete,
  };
};
